// src/pages/compony/Settings.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../../core/api';
import './Settings.css';
import SettingsSkeleton from './SettingsSkeleton';
import FileUploader from './FileUploader';
import BaleConnect from './BaleConnect';
import BanksSection from './BanksSection';

const EMPTY_SELLER = {
  name: '', national: '', eco: '', postal: '', province: '', city: '',
  address: '', phone: '', landline: '', email: '', website: '',
};
const EMPTY_FILES = { logo: '', stamp: '', signature: '' };
const Settings = () => {
  const [seller, setSeller] = useState(EMPTY_SELLER);
  const [files, setFiles] = useState(EMPTY_FILES);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');

  const [isDirty, setIsDirty] = useState(false);
  const isDirtyRef = useRef(false);
  const [storage, setStorage] = useState(null);

  const stored = JSON.parse(localStorage.getItem('user') || 'null');
  const isOwner = (stored?.user || stored)?.role === 'owner';

  const loadStorage = useCallback(async () => {
    try {
      const res = await api.get('/storage');
      setStorage(res.data);
    } catch (err) {
      console.error('خطا در دریافت فضای ذخیره‌سازی:', err);
    }
  }, []);

  useEffect(() => {
    loadStorage();
  }, [loadStorage]);
  const showMessage = useCallback((text, type = 'success') => {
    setMessage(text);
    setMessageType(type);
  }, []);

  /* ============== Fetch ============== */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await api.get('/company');
        const c = response.data?.company || {};
        if (cancelled) return;
        const { logo, stamp, signature, ...info } = c;
        setSeller((prev) => ({ ...prev, ...info }));
        setFiles({ logo: logo || '', stamp: stamp || '', signature: signature || '' });
      } catch (err) {
        if (cancelled) return;
        console.error('خطا در دریافت اطلاعات فروشنده:', err);
        showMessage('خطا در دریافت اطلاعات. لطفاً صفحه را رفرش کنید.', 'danger');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [showMessage]);

  /* ============== Toast auto-hide ============== */
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => setMessage(''), 3000);
    return () => clearTimeout(t);
  }, [message]);

  /* ============== beforeunload ============== */
  useEffect(() => {
    const handler = (e) => {
      if (!isDirtyRef.current) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  /* ============== Handlers ============== */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setSeller((prev) => ({ ...prev, [name]: value }));
    if (!isDirtyRef.current) {
      isDirtyRef.current = true;
      setIsDirty(true);
    }
  };

  const setFile = (field) => (url) => {
    setFiles((prev) => ({ ...prev, [field]: url }));
    loadStorage();
      window.dispatchEvent(new Event("company-updated"));
  };

  /* ============== Submit (فقط اطلاعات متنی) ============== */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!seller.name?.trim()) {
      showMessage('نام شخص حقیقی/حقوقی الزامی است', 'danger');
      return;
    }
    setSaving(true);
    try {
      const payload = {};
      Object.keys(EMPTY_SELLER).forEach((k) => {
        payload[k] = seller[k] ?? '';
      });
      await api.post('/company', payload);

      isDirtyRef.current = false;
      setIsDirty(false);
      showMessage('مشخصات با موفقیت ذخیره شد', 'success');
    } catch (err) {
      console.error('خطا در ذخیره اطلاعات:', err);
      showMessage(
        err.response?.data?.error || 'خطا در ذخیره اطلاعات. لطفاً دوباره تلاش کنید.',
        'danger'
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <SettingsSkeleton />;

  const field = (name, label, extra = {}, full = false) => (
    <div className={full ? 'field-full' : undefined}>
      <label>{label}</label>
      <input name={name} value={seller[name] || ''} onChange={handleChange} {...extra} />
    </div>
  );
  const ltr = { style: { direction: 'ltr', textAlign: 'right' } };

  return (
    <>
      {message && (
        <div className={messageType === 'danger' ? 'toast-danger' : 'toast-success'}>
          {message}
        </div>
      )}

     
      {/* ============ اطلاعات فروشنده ============ */}
      <div className="card">
        <h2>مشخصات فروشنده (روی همه فاکتورها چاپ می‌شود)</h2>

        {isDirty && (
          <div className="dirty-banner">
            <i className="fa-solid fa-triangle-exclamation" />
            تغییرات ذخیره نشده دارید. لطفاً قبل از خروج ذخیره کنید.
          </div>
        )}

        <form onSubmit={handleSubmit} id="settingsForm">
          <div className="grid">
            {field('name', 'نام شخص حقیقی/حقوقی')}
            {field('national', 'شماره ثبت/شماره ملی')}
            {field('eco', 'شماره اقتصادی')}
            {field('postal', 'کد پستی')}
            {field('province', 'استان')}
            {field('city', 'شهرستان')}
            {field('address', 'نشانی کامل', {}, true)}
            {field('phone', 'شماره تلفن')}
            {field('landline', 'تلفن ثابت', { placeholder: '02112345678', ...ltr })}
            {field('email', 'ایمیل', { type: 'email', placeholder: 'info@example.com', ...ltr })}
            {field('website', 'آدرس سایت', { placeholder: 'www.example.com', ...ltr })}

          </div>

          <div className="flexend">
            <button className="btn" type="submit" disabled={saving}>
              {!saving && <i className="fa-solid fa-floppy-disk" />}
              <span className="btn-label">
                {saving ? 'در حال ذخیره...' : 'ذخیره مشخصات فروشنده'}
              </span>
              {saving && (
                <span className="btn-spinner">
                  <svg width="16" height="16" viewBox="0 0 50 50" style={{ animation: 'spin 0.8s linear infinite' }}>
                    <circle cx="25" cy="25" r="20" fill="none" stroke="currentColor"
                            strokeWidth="5" strokeLinecap="round" strokeDasharray="90 150" />
                  </svg>
                </span>
              )}
            </button>
          </div>
        </form>
      </div>
<BanksSection onMessage={showMessage} />
       {/* ============ تصاویر (آپلود فوری) ============ */}
      <div className="card">
        <h2>تصاویر شرکت</h2>
        <p className="section-sub">
          با انتخاب یا رها کردن تصویر، پس از برش، بلافاصله ذخیره می‌شود؛ نیازی به دکمه ذخیره نیست.
        </p>

                {storage && (() => {
          const percent = Math.min((storage.used / storage.limit) * 100, 100);
          const full = percent >= 90;
          return (
            <div className="storage-box">
              <div className="storage-head">
                <span>فضای مصرفی</span>
                <b style={{ direction: 'ltr' }}>
                  {(storage.used / 1048576).toLocaleString('fa-IR', { maximumFractionDigits: 1 })}
                  {' از '}
                  {(storage.limit / 1048576).toLocaleString('fa-IR', { maximumFractionDigits: 0 })}
                  {' مگابایت'}
                </b>
              </div>
              <div className="storage-track">
                <div
                  className={`storage-fill ${full ? 'danger' : ''}`}
                  style={{ width: `${percent}%` }}
                />
              </div>
              {full && (
                <div className="storage-warn">
                  <i className="fa-solid fa-triangle-exclamation" /> فضای شما رو به اتمام است.
                </div>
              )}
            </div>
          );
        })()}
        <div className="upload-section">
          <FileUploader
            label="لوگو شرکت" field="logo" icon="fa-building"
            aspect={1} outW={512} outH={512} hint="نسبت ۱:۱ — ۵۱۲×۵۱۲"
            currentUrl={files.logo} onChange={setFile('logo')} onMessage={showMessage}
          />
          <FileUploader
            label="مهر شرکت" field="stamp" icon="fa-stamp"
            aspect={1} outW={512} outH={512} hint="نسبت ۱:۱ — ۵۱۲×۵۱۲"
            currentUrl={files.stamp} onChange={setFile('stamp')} onMessage={showMessage}
          />
          <FileUploader
            label="امضای مدیر" field="signature" icon="fa-signature"
      aspect={1} outW={512} outH={512} hint="نسبت ۱:۱ — ۵۱۲×۵۱۲"

            currentUrl={files.signature} onChange={setFile('signature')} onMessage={showMessage}
          />
        </div>
      </div>


{isOwner && <BaleConnect onMessage={showMessage} />}
    </>
  );
};

export default Settings;